import { For, Index, Match, Show, Switch } from "solid-js";
import { Dynamic } from "solid-js/web";
import { BrandGlyph } from "~/components/marketing/BrandGlyph";
import { CUSTOMER_LOGOS, METRICS, type Metric } from "~/components/marketing/content";
import { type Scene, useCountUp, useScene } from "~/hooks/useScene";
import { useI18n } from "~/i18n";
import { Marquee } from "./Marquee";
import "~/components/marketing/mockups/mockups.css";

/** The customer logo strip, then the headline numbers as cards that count up on arrival. */
export const ProofStrip = () => {
  const { t } = useI18n();
  const scene = useScene();
  return (
    <section aria-labelledby="home-logos" class="border-y border-primary-border bg-panel px-4 py-12 sm:px-6 lg:px-8">
      <div class="mx-auto max-w-7xl">
        <h2
          id="home-logos"
          class="text-center font-mono text-xs font-semibold tracking-[0.18em] text-secondary-text uppercase"
        >
          {t("marketing.home.logos.title")}
        </h2>
        <div class="mt-6">
          <Marquee each={CUSTOMER_LOGOS} label={t("marketing.home.logos.title")}>
            {(logo) => (
              <span class="logo-ink mx-6 flex" style={{ "--logo": logo.color }}>
                <BrandGlyph src={logo.src} label={logo.name} class="h-8 w-28" />
              </span>
            )}
          </Marquee>
        </div>
        <dl
          ref={scene.ref}
          class="mx-auto mt-8 grid max-w-5xl gap-3 sm:grid-cols-3"
          classList={{ "mk-armed": scene.armed(), "in-view": scene.inView() }}
        >
          <For each={METRICS}>{(metric) => <MetricCard metric={metric} scene={scene} />}</For>
        </dl>
      </div>
    </section>
  );
};

const MetricCard = (props: { metric: Metric; scene: Scene }) => {
  const { t } = useI18n();
  const count = useCountUp(props.metric.value, props.scene);
  return (
    <div
      class="flex flex-col-reverse gap-1.5 rounded-2xl border border-primary-border bg-tile bg-[radial-gradient(ellipse_at_top_right,color-mix(in_srgb,var(--c)_9%,transparent),transparent_65%)] px-5 py-4"
      style={{ "--c": props.metric.color }}
    >
      <dt class="text-sm leading-snug text-secondary-text">
        {props.metric.label}
        <Show when={props.metric.placeholder}>
          <span class="ms-2 inline-block rounded bg-primary-border/60 px-1.5 py-px align-middle font-mono text-[10px] font-semibold tracking-wider text-secondary-text uppercase dark:bg-primary-border">
            {t("marketing.home.metrics.placeholder")}
          </span>
        </Show>
      </dt>
      <dd class="flex items-center justify-between gap-4">
        <span class="text-4xl font-semibold tracking-tight tabular-nums">
          <span aria-hidden="true">
            {count()}
            {props.metric.unit}
          </span>
          <span class="sr-only">
            {props.metric.value}
            {props.metric.unit}
          </span>
        </span>
        <MetricVisual visual={props.metric.visual} />
      </dd>
    </div>
  );
};

const W = 84;
const H = 36;

/** The card's small picture, drawn in the metric's colour; it plays with the count-up. */
const MetricVisual = (props: { visual: Metric["visual"] }) => (
  <Switch>
    <Match when={props.visual.kind === "icon" && props.visual}>
      {(visual) => (
        <span
          aria-hidden="true"
          class="mk-ink pop-in grid size-10 place-items-center rounded-xl bg-(--c)/12 ring-1 ring-(--c)/20 dark:bg-(--c)/20"
          style={{ "animation-delay": "0.3s" }}
        >
          <Dynamic component={visual().icon} class="size-5" />
        </span>
      )}
    </Match>
    <Match when={props.visual.kind === "bars" && props.visual}>
      {(visual) => {
        const max = Math.max(...visual().points);
        const step = W / visual().points.length;
        return (
          <svg aria-hidden="true" viewBox={`0 0 ${W} ${H}`} class="mk-ink h-9 w-21 shrink-0 overflow-visible">
            <Index each={visual().points}>
              {(point, i) => (
                <rect
                  class="rail-grow"
                  x={i * step + 1}
                  y={H - (point() / max) * H}
                  width={step - 4}
                  height={(point() / max) * H}
                  rx="2"
                  fill="currentColor"
                  opacity={i === visual().points.length - 1 ? 1 : 0.3}
                  style={{
                    "--gy": "0",
                    "transform-box": "fill-box",
                    "transform-origin": "bottom",
                    "animation-delay": `${0.2 + i * 0.12}s`,
                  }}
                />
              )}
            </Index>
          </svg>
        );
      }}
    </Match>
    <Match when={props.visual.kind === "line" && props.visual}>
      {(visual) => {
        const max = Math.max(...visual().points);
        const last = visual().points.length - 1;
        const xy = (point: number, i: number) => [(i / last) * W, H - 3 - (point / max) * (H - 6)] as const;
        const line = visual()
          .points.map((point, i) => xy(point, i).join(","))
          .join(" L");
        const [endX, endY] = xy(visual().points[last], last);
        return (
          <svg aria-hidden="true" viewBox={`0 0 ${W} ${H}`} class="mk-ink h-9 w-21 shrink-0 overflow-visible">
            <path
              class="mark-in"
              d={`M${line} L${W},${H} L0,${H} Z`}
              fill="currentColor"
              fill-opacity="0.14"
              style={{ "animation-delay": "0.9s" }}
            />
            <path
              class="draw-in"
              d={`M${line}`}
              pathLength="100"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <circle
              class="pop-in"
              cx={endX}
              cy={endY}
              r="3"
              fill="currentColor"
              style={{ "transform-box": "fill-box", "transform-origin": "center", "animation-delay": "1.3s" }}
            />
          </svg>
        );
      }}
    </Match>
  </Switch>
);
